import React from "react";
import _ from "lodash";
import numeral from "numeral";
import ModalWrapper from "components/common/ModalWrapper";
import HighchartWrapper from "components/common/HighchartWrapper";
import SwitchTabs from "components/common/SwitchTabs";
import {
  buttonActionClassGray,
  buttonActionDisableGray,
} from "helpers/StyleClass";
import { getDateList } from "helpers/DateTransform";
import { pbTypeReportKey } from "./AnatomyCstReportHelper";
import { DEMAND_TYPES_ABBR, DEMAND_TYPES } from "constants/DemandType";
import { DEMAND_TYPE_COLORS } from "components/inventory-rev/DataHelper";

import { useAnatomyData, useAnatomyFilter } from "./context/AnatomyContexts";

const METRIC = {
  REVENUE: "rev",
  IMPRESSION: "imp",
};

class DemandTypePortfolioButtonInner extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isOpenModal: false,
    };
  }

  render() {
    const { isOpenModal } = this.state;
    const {
      data,
      uprSegmentId,
      items,
      noPbType,
      startDate,
      endDate,
      includeToday,
    } = this.props;

    return (
      <>
        <button
          type="button"
          className={
            uprSegmentId === -1
              ? buttonActionDisableGray
              : buttonActionClassGray
          }
          onClick={() => this.setState({ isOpenModal: true })}
          disabled={uprSegmentId === -1}
        >
          Demand Type Portfolio
        </button>

        {isOpenModal && (
          <DemandTypePortfolioModal
            onClose={() => this.setState({ isOpenModal: false })}
            data={data}
            uprSegmentId={uprSegmentId}
            items={items}
            noPbType={noPbType}
            startDate={startDate}
            endDate={endDate}
            includeToday={includeToday}
          ></DemandTypePortfolioModal>
        )}
      </>
    );
  }
}

class DemandTypePortfolioModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      typeOptions: [
        {
          label: "All",
          value: pbTypeReportKey.ALL,
        },
        {
          label: "No PB",
          value: pbTypeReportKey.NO_PB,
        },
        {
          label: "No PB key20",
          value: pbTypeReportKey.NO_PB_KEY_20,
        },
      ],
      metricOptions: [
        {
          label: "Revenue",
          value: METRIC.REVENUE,
        },
        {
          label: "Impression",
          value: METRIC.IMPRESSION,
        },
      ],
      selectedType: props.noPbType
        ? pbTypeReportKey[props.noPbType]
        : pbTypeReportKey.ALL,
      selectedMetric: METRIC.REVENUE,

      layerallOption: null,
      layer1Option: null,
      layer2Option: null,

      data: null,
      dateList: [],
      hasLayer2: null,
    };

    this.handleChartChanged = this.handleChartChanged.bind(this);
    this._getChartOption = this._getChartOption.bind(this);
  }

  componentDidMount() {
    const { startDate, endDate, includeToday, data, uprSegmentId } = this.props;
    const { typeOptions, selectedType } = this.state;

    const parsedTypeOptions = _.map(typeOptions, (option) => {
      option.isNoData = data[option.value].length === 0;
      return option;
    });

    const filteredTypeOptions = _.filter(
      parsedTypeOptions,
      (option) => !option.isNoData
    );

    const selectedTypeInOptions = _.find(filteredTypeOptions, {
      value: selectedType,
    });

    const dateList = getDateList(
      startDate.split(" ")[0],
      endDate.split(" ")[0],
      includeToday
    );

    const dataKey = [];
    ["1", "2"].forEach((layer) => {
      _.values(DEMAND_TYPES_ABBR).forEach((abbr) => {
        ["rev", "imp"].forEach((metric) => {
          const key = `layer${layer}_${abbr}_${metric}`;
          dataKey.push(key);
        });
      });
    });

    // [type]: { [b/o]:{ [layer]-[demandTypeAbbr]-[metric]: [] as value by date } }
    const result = {};
    const hasLayer2 = {};
    [
      pbTypeReportKey.ALL,
      pbTypeReportKey.NO_PB,
      pbTypeReportKey.NO_PB_KEY_20,
    ].forEach((type) => {
      result[type] = {};
      hasLayer2[type] = _.some(data[type], (item) => item.layer2_req > 0);

      const filteredData = _.filter(data[type], (item) => {
        return (
          ["b", "o"].includes(item.group_type) &&
          item.upr_segment_id === uprSegmentId
        );
      });

      const groupedData = _.groupBy(filteredData, (item) => {
        const day = item.date.split(" ")[0];
        return `${day}-${item.group_type}`;
      });

      const sumData = _.mapValues(groupedData, (items) => {
        return _.mapValues(_.keyBy(dataKey), (key) => {
          const list = _.map(items, (item) => item[key] || 0);
          return _.sum(list);
        });
      });

      _.forEach(["b", "o"], (group) => {
        result[type][group] = {};
        _.forEach(dataKey, (key) => {
          const dataByDate = _.map(dateList, (date) => {
            return sumData[`${date}-${group}`]
              ? sumData[`${date}-${group}`][key]
              : 0;
          });
          result[type][group][key] = dataByDate;
          const allKey = ["layerall", ..._.split(key, "_").slice(1)].join("_");
          if (!result[type][group][allKey]) {
            result[type][group][allKey] = dataByDate;
          } else {
            result[type][group][allKey] = _.map(
              result[type][group][allKey],
              (v, idx) => {
                return v + dataByDate[idx];
              }
            );
          }
        });
      });
    });

    this.setState(
      {
        dateList,
        data: result,
        hasLayer2,
        typeOptions: parsedTypeOptions,
        selectedType:
          selectedTypeInOptions.isNoData && filteredTypeOptions.length > 0
            ? filteredTypeOptions[0].value
            : selectedType,
      },
      () => {
        this.handleChartChanged();
      }
    );
  }

  handleChartChanged() {
    const layerallOption = this._getChartOption("layerall");
    const layer1Option = this._getChartOption("layer1");
    const layer2Option = this._getChartOption("layer2");

    this.setState({
      layerallOption,
      layer1Option,
      layer2Option,
    });
  }

  _getChartOption(layer) {
    const {
      metricOptions,
      selectedType,
      selectedMetric,
      data,
      dateList,
      hasLayer2,
    } = this.state;

    if (layer === "layer2" && !hasLayer2[selectedType]) return null;
    if (layer === "layerall" && !hasLayer2[selectedType]) return null;

    const parsedData = data ? data[selectedType] : null;
    if (!parsedData) return null;

    const valueFormat = selectedMetric === METRIC.REVENUE ? "0,0.00" : "0,0";

    return {
      chart: {
        type: "column",
        height: 320,
        events: {
          render: function () {
            const chart = this;
            chart.legend.allItems.forEach((item) => {
              if (item.legendItem && !item._hasLegendEvents) {
                item.legendItem.on("mouseover", function () {
                  const check = _.filter(chart.series, (s) => {
                    return s.name === item.name && !s.userOptions.disabled;
                  });
                  if (check.length === 0) return;

                  chart.series.forEach((s) => {
                    if (s.name === item.name) {
                      s.points.forEach((p) => {
                        if (p && p.setState) p.setState("hover");
                      });
                    } else {
                      if (s.userOptions.stack === "o") {
                        s.points.forEach((p) => {
                          if (p && p.setState) p.setState("inactive");
                        });
                      }
                    }
                  });
                });
                item.legendItem.on("mouseout", function () {
                  chart.series.forEach((s) => {
                    s.points.forEach((p) => {
                      if (p && p.setState) p.setState("");
                    });
                  });
                });

                item._hasLegendEvents = true;
              }
            });
          },
        },
      },
      title: {
        text: layer,
        align: "left",
      },
      xAxis: {
        categories: dateList,
      },
      yAxis: {
        title: {
          text: _.find(metricOptions, { value: selectedMetric }).label,
        },
      },
      tooltip: {
        formatter: function () {
          const chart = this.series.chart;
          const point = this.point;
          const seriesName = this.series.name;

          const sameNameSeries = chart.series.filter(
            (s) => s.name === seriesName
          );

          let html = `<b>${point.category}</b><br/>`;

          sameNameSeries.forEach((s) => {
            const p = s.data[point.index];
            if (p && typeof p.y === "number") {
              const percent = !_.isNil(p.percentage)
                ? `${numeral(p.percentage).format("0.0")}%`
                : "";
              html += `[${s.options.stack}] ${
                s.name
              }: <b>${percent}</b> (${numeral(p.y).format(valueFormat)})<br/>`;
            }
          });

          return html;
        },
      },
      plotOptions: {
        column: {
          stacking: "percent",
        },
        series: {
          events: {
            legendItemClick: function () {
              const chart = this.chart;
              const sameNameSeries = chart.series.filter(
                (s) => s.name === this.name
              );
              const allZero = sameNameSeries.every((s) =>
                s.yData.every((v) => v === 0)
              );
              if (allZero) {
                return false;
              }
            },
          },
        },
      },
      series: _.flattenDeep(
        _.values(DEMAND_TYPES).map((dt) => {
          const isHide =
            _.sum([
              ...parsedData["b"][
                `${layer}_${DEMAND_TYPES_ABBR[dt]}_${selectedMetric}`
              ],
              ...parsedData["o"][
                `${layer}_${DEMAND_TYPES_ABBR[dt]}_${selectedMetric}`
              ],
            ]) === 0;
          return ["b", "o"].map((group) => {
            return {
              ...(group === "b" ? { id: dt } : {}),
              name: dt,
              data: parsedData[group][
                `${layer}_${DEMAND_TYPES_ABBR[dt]}_${selectedMetric}`
              ].map((d) => d),
              color: DEMAND_TYPE_COLORS.get(dt),
              stack: group,
              ...(group === "o" ? { linkedTo: dt, showInLegend: false } : {}),
              dataLabels: {
                enabled: true,
                verticalAlign: "top",
                inside: false,
                formatter: function () {
                  if (this.series.index === 0 || this.series.index === 1)
                    return this.series.options.stack;
                },
              },
              disabled: isHide,
              visible: !isHide,
            };
          });
        })
      ),
    };
  }

  render() {
    const { onClose, items, uprSegmentId } = this.props;
    const {
      typeOptions,
      metricOptions,
      selectedType,
      selectedMetric,

      layerallOption,
      layer1Option,
      layer2Option,
    } = this.state;

    const segment = _.find(items, { upr_segment_id: uprSegmentId });

    return (
      <ModalWrapper
        isOpen={true}
        handleClose={onClose}
        showCloseFooter={true}
        title={{
          label: "Demand Type Portfolio ",
          highlight: `${segment.upr_segment_id} ${segment.name}`,
        }}
        customOverlayStyle={{ zIndex: 100 }}
        width="70%"
      >
        <div className="overflow-y-auto pr-2" style={{ maxHeight: "640px" }}>
          <div className="mb-1 flex items-end justify-between">
            <div className="flex gap-1">
              {typeOptions.map((option) => (
                <button
                  disabled={selectedType === option.value || option.isNoData}
                  type="button"
                  className={`rounded mr-1 px-4 py-1 font-semibold ${
                    option.isNoData
                      ? "cursor-not-allowed bg-gray-100 text-gray-500"
                      : selectedType === option.value
                      ? "bg-blue-200 text-gray-700"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-800"
                  }`}
                  onClick={() =>
                    this.setState({ selectedType: option.value }, () =>
                      this.handleChartChanged()
                    )
                  }
                  key={option.value}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div>
              <SwitchTabs
                tabs={metricOptions}
                selectedTabKey={selectedMetric}
                handleChange={(value) => {
                  this.setState({ selectedMetric: value }, () =>
                    this.handleChartChanged()
                  );
                }}
              ></SwitchTabs>
            </div>
          </div>
          {layerallOption && (
            <HighchartWrapper
              options={layerallOption}
              key={`layerall-${selectedType}-${selectedMetric}`}
            ></HighchartWrapper>
          )}
          {layer1Option && (
            <HighchartWrapper
              options={layer1Option}
              key={`layer1-${selectedType}-${selectedMetric}`}
            ></HighchartWrapper>
          )}
          {layer2Option && (
            <HighchartWrapper
              options={layer2Option}
              key={`layer2-${selectedType}-${selectedMetric}`}
            ></HighchartWrapper>
          )}
        </div>
      </ModalWrapper>
    );
  }
}

const DemandTypePortfolioButton = React.memo((props) => {
  const dataContext = useAnatomyData();
  const filterContext = useAnatomyFilter();

  if (!dataContext || !filterContext) {
    return <DemandTypePortfolioButtonInner {...props} />;
  }

  return (
    <DemandTypePortfolioButtonInner
      {...props}
      data={dataContext.reports}
      uprSegmentId={dataContext.selectedUprSegmentId}
      items={dataContext.filteredData.upr_segments}
      noPbType={filterContext.noPbType}
      startDate={filterContext.startDate}
      endDate={filterContext.endDate}
      includeToday={filterContext.includeToday}
    />
  );
});

export default DemandTypePortfolioButton;
